import { useState } from "react";
import { useParams, useNavigate, Link, Navigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Layout } from "@/components/layout/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { HalfStarRating } from "@/components/reviews/HalfStarRating";
import { BooleanQuestion } from "@/components/reviews/BooleanQuestion";

export default function EditReview() {
  const { id } = useParams<{ id: string }>();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: review, isLoading } = useQuery({
    queryKey: ["edit-review", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("reviews")
        .select("*, venues(name, slug, city)")
        .eq("id", id!)
        .eq("user_id", user!.id)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!user && !!id,
  });

  const [form, setForm] = useState<Record<string, any> | null>(null);

  // Initialize form from loaded review
  const getForm = () => {
    if (form) return form;
    if (!review) return null;
    const r = review as any;
    return {
      event_date: review.event_date ?? "",
      price_paid: review.price_paid != null ? String(review.price_paid) : "",
      overall_rating: Number(review.overall_rating) || 0,
      sound_rating: Number(review.sound_rating) || 0,
      staff_rating: Number(review.staff_rating) || 0,
      sound_tech: review.sound_tech,
      sound_tech_notes: review.sound_tech_notes ?? "",
      technical_rider: review.technical_rider,
      technical_rider_notes: review.technical_rider_notes ?? "",
      merchandising_area: review.merchandising_area,
      merch_area_details: r.merch_area_details ?? "",
      ticketing_required: review.ticketing_required,
      ticketing_provider: review.ticketing_provider ?? "",
      backline_available: r.backline_available ?? null,
      backline_details: r.backline_details ?? "",
      audio_recording: r.audio_recording ?? null,
      poster_design: r.poster_design ?? null,
      comment: review.comment ?? "",
    };
  };

  const currentForm = getForm();

  const update = (key: string, value: any) => {
    setForm((prev) => ({ ...(prev ?? currentForm!), [key]: value }));
  };

  const mutation = useMutation({
    mutationFn: async () => {
      const f = form ?? currentForm!;
      // Save revision snapshot first
      const { data: revCount } = await supabase
        .from("review_revisions")
        .select("revision_number")
        .eq("review_id", id!)
        .order("revision_number", { ascending: false })
        .limit(1);

      const nextRevision = (revCount?.[0]?.revision_number ?? 0) + 1;

      await supabase.from("review_revisions").insert({
        review_id: id!,
        edited_by_user_id: user!.id,
        revision_number: nextRevision,
        payload_json: review as any,
      });

      // Update the review
      const { error } = await supabase
        .from("reviews")
        .update({
          event_date: f.event_date || null,
          price_paid: f.price_paid ? parseFloat(f.price_paid) : null,
          overall_rating: f.overall_rating,
          sound_rating: f.sound_rating || null,
          staff_rating: f.staff_rating || null,
          sound_tech: f.sound_tech,
          sound_tech_notes: f.sound_tech_notes || null,
          technical_rider: f.technical_rider,
          technical_rider_notes: f.technical_rider_notes || null,
          merchandising_area: f.merchandising_area,
          merch_area_details: f.merch_area_details || null,
          ticketing_required: f.ticketing_required,
          ticketing_provider: f.ticketing_provider || null,
          backline_available: f.backline_available,
          backline_details: f.backline_details || null,
          audio_recording: f.audio_recording,
          poster_design: f.poster_design,
          comment: f.comment,
          is_edited: true,
          edited_at: new Date().toISOString(),
        } as any)
        .eq("id", id!)
        .eq("user_id", user!.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["my-review-detail", id] });
      queryClient.invalidateQueries({ queryKey: ["my-reviews"] });
      toast({ title: "Review actualizada" });
      navigate(`/mi-cuenta/reviews/${id}`);
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  if (authLoading || isLoading) {
    return <Layout><div className="container py-8"><Skeleton className="h-64" /></div></Layout>;
  }
  if (!user) return <Navigate to="/auth" />;
  if (!review || !currentForm) {
    return <Layout><div className="container py-8"><p>Review no encontrada.</p></div></Layout>;
  }

  const venue = review.venues as any;
  const f = form ?? currentForm;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (f.overall_rating === 0) {
      toast({ title: "Selecciona una valoración general", variant: "destructive" });
      return;
    }
    if (!f.event_date) {
      toast({ title: "La fecha del evento es obligatoria", variant: "destructive" });
      return;
    }
    mutation.mutate();
  };

  return (
    <Layout>
      <div className="container max-w-2xl py-6">
        <Link
          to={`/mi-cuenta/reviews/${id}`}
          className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Volver al detalle
        </Link>

        <Card>
          <CardHeader>
            <CardTitle className="font-display">Editar review de {venue?.name ?? "..."}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Ratings */}
              <div className="grid gap-4 sm:grid-cols-2">
                <HalfStarRating
                  label="Valoración general"
                  value={f.overall_rating}
                  onChange={(v) => update("overall_rating", v)}
                  required
                />
                <HalfStarRating
                  label="Sonido"
                  value={f.sound_rating}
                  onChange={(v) => update("sound_rating", v)}
                />
                <HalfStarRating
                  label="Staff"
                  value={f.staff_rating}
                  onChange={(v) => update("staff_rating", v)}
                />
              </div>

              {/* Event details */}
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Fecha del evento *</Label>
                  <Input
                    type="date"
                    value={f.event_date}
                    onChange={(e) => update("event_date", e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Precio pagado (€)</Label>
                  <Input
                    type="number"
                    placeholder="0"
                    value={f.price_paid}
                    onChange={(e) => update("price_paid", e.target.value)}
                  />
                </div>
              </div>

              {/* Boolean fields */}
              <div className="space-y-4 rounded-lg border p-4">
                <p className="text-sm font-medium">Información operativa</p>
                <div className="space-y-3">
                  <BooleanQuestion
                    label="¿Técnico de sonido propio?"
                    value={f.sound_tech}
                    onChange={(v) => update("sound_tech", v)}
                  />
                  <BooleanQuestion
                    label="¿Rider técnico disponible?"
                    value={f.technical_rider}
                    onChange={(v) => update("technical_rider", v)}
                    detailValue={f.technical_rider_notes}
                    onDetailChange={(v) => update("technical_rider_notes", v)}
                    detailPlaceholder="Notas sobre el rider técnico..."
                  />
                  <BooleanQuestion
                    label="¿Zona de merchandising?"
                    value={f.merchandising_area}
                    onChange={(v) => update("merchandising_area", v)}
                    detailValue={f.merch_area_details}
                    onDetailChange={(v) => update("merch_area_details", v)}
                    detailPlaceholder="Detalles sobre la zona de merch..."
                  />
                  <BooleanQuestion
                    label="¿Ticketing obligatorio?"
                    value={f.ticketing_required}
                    onChange={(v) => update("ticketing_required", v)}
                    detailValue={f.ticketing_provider}
                    onDetailChange={(v) => update("ticketing_provider", v)}
                    detailPlaceholder="Proveedor de ticketing..."
                  />

                  <BooleanQuestion
                    label="¿Hubo backline disponible?"
                    value={f.backline_available}
                    onChange={(v) => update("backline_available", v)}
                    detailValue={f.backline_details}
                    onDetailChange={(v) => update("backline_details", v)}
                    detailPlaceholder="Detalles del backline..."
                  />

                  <BooleanQuestion
                    label="¿Grabación de audio?"
                    value={f.audio_recording}
                    onChange={(v) => update("audio_recording", v)}
                  />

                  <BooleanQuestion
                    label="¿Diseño de cartelería?"
                    value={f.poster_design}
                    onChange={(v) => update("poster_design", v)}
                  />
                </div>
              </div>

              <Button type="submit" className="w-full" disabled={mutation.isPending}>
                {mutation.isPending ? "Guardando..." : "Guardar cambios"}
              </Button>
              <p className="text-center text-xs text-muted-foreground">
                La review editada mantendrá su estado actual de publicación.
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
